import React, { useState, useEffect, useRef } from "react";
import {
  Spinner,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Card,
  CardBody,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Progress,
} from "reactstrap";
import { getMantisAgentsWithDecryptedDataByBusinessId } from "../../../ApiCalls/mantisAgents";
import {
  getMantisConversationsByBusinessId,
  getMantisConversationMessagesByConversationId,
  getMantisConversationSessionsByConversationId,
} from "../../../ApiCalls/mantisConversations";
import { sendBulkTemplates } from "../../../ApiCalls/bulkTemplate";
import { useBatchAIAnalysis } from "../../../hooks/useBatchAIAnalysis";
import { analyzeConversation } from "../../../utils/conversationAnalysis";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import * as XLSX from "xlsx";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("es-ES", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateForFilterDisplay = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
};

const getAgentTypeLabel = (type) => {
  const typeLabels = {
    GENERAL: "General",
    INITIAL_PURCHASE_REQUESTS: "Solicitudes de Compra",
    SUPPLIER_QUOTATIONS: "Cotizaciones de Proveedores",
    WHATSAPP_SUPPLIER_QUOTATIONS: "Cotizaciones WhatsApp",
  };
  return typeLabels[type] || type || "—";
};

const BusinessAgentsSection = ({ token, mantisBusinessId }) => {
  // Bulk template sending is switched off for Tangelo, so the whole "Acciones" column
  // of the agents table — its only content — is dropped for them.
  const showBulkTemplates = mantisBusinessId !== "66bbb0a2556f62f4d3b49f69";

  const [activeTab, setActiveTab] = useState("conversations");
  const [agents, setAgents] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Conversation detail modal (view only)
  const [conversationModal, setConversationModal] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [conversationSessions, setConversationSessions] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const messagesScrollRef = useRef(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  // Bulk template (envío masivo) modal
  const [bulkTemplateModal, setBulkTemplateModal] = useState(false);
  const [selectedWhatsAppAccount, setSelectedWhatsAppAccount] = useState(null);
  const [bulkTemplateAgent, setBulkTemplateAgent] = useState(null);
  const [bulkTemplateForm, setBulkTemplateForm] = useState({
    templateName: "",
    excelFile: null,
    language: "es_MX",
  });
  const [bulkTemplateLoading, setBulkTemplateLoading] = useState(false);
  const [bulkTemplateError, setBulkTemplateError] = useState(null);
  const [bulkTemplateSuccess, setBulkTemplateSuccess] = useState(null);
  const [bulkTemplateResults, setBulkTemplateResults] = useState(null);
  const [showDetailedResults, setShowDetailedResults] = useState(false);
  const [
    bulkTemplateCompletionBannerDismissed,
    setBulkTemplateCompletionBannerDismissed,
  ] = useState(false);
  const [bulkTemplateProgress, setBulkTemplateProgress] = useState({
    currentBatch: 0,
    totalBatches: 0,
    currentSent: 0,
    currentFailed: 0,
    totalRecipients: 0,
  });

  const [exportLoading, setExportLoading] = useState(false);

  // Date filter for Conversaciones tab (sent to backend)
  const [dateFilterFrom, setDateFilterFrom] = useState("");
  const [dateFilterTo, setDateFilterTo] = useState("");

  // Pagination for Conversaciones tab (server-side)
  const [conversationsPage, setConversationsPage] = useState(1);
  const [conversationsPageSize, setConversationsPageSize] = useState(20); // input value (can be editing)
  const [conversationsPageSizeApplied, setConversationsPageSizeApplied] =
    useState(20); // applied after 2s debounce
  const [conversationsTotal, setConversationsTotal] = useState(0);
  const [conversationsPerDayFromBackend, setConversationsPerDayFromBackend] =
    useState([]);
  const [
    tagConversationsPerDayFromBackend,
    setTagConversationsPerDayFromBackend,
  ] = useState([]);
  const [sessionsPerDayFromBackend, setSessionsPerDayFromBackend] = useState(
    [],
  );
  const [tagFilter, setTagFilter] = useState("");
  const [tagFilterInput, setTagFilterInput] = useState("");
  const [phoneFilterInput, setPhoneFilterInput] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const dateFromInputRef = useRef(null);
  const dateToInputRef = useRef(null);
  const CONVERSATIONS_PAGE_SIZE_MIN = 1;
  const CONVERSATIONS_PAGE_SIZE_MAX = 200;
  const DEBOUNCE_MS_PAGE_SIZE = 1500;
  const pageSize = Math.min(
    Math.max(conversationsPageSizeApplied, CONVERSATIONS_PAGE_SIZE_MIN),
    CONVERSATIONS_PAGE_SIZE_MAX,
  );

  // Debounce: apply new page size 2 seconds after user stops typing
  useEffect(() => {
    const clamped = Math.min(
      Math.max(conversationsPageSize, CONVERSATIONS_PAGE_SIZE_MIN),
      CONVERSATIONS_PAGE_SIZE_MAX,
    );
    if (clamped === conversationsPageSizeApplied) return;
    const t = setTimeout(() => {
      setConversationsPageSize(clamped);
      setConversationsPageSizeApplied(clamped);
      setConversationsPage(1);
    }, DEBOUNCE_MS_PAGE_SIZE);
    return () => clearTimeout(t);
  }, [conversationsPageSize, conversationsPageSizeApplied]);

  // Debounce tag filter so typing doesn't refetch on every keypress
  useEffect(() => {
    const value = tagFilterInput.trim();
    if (value === tagFilter) return;
    const t = setTimeout(() => {
      setTagFilter(value);
      setConversationsPage(1);
    }, 700);
    return () => clearTimeout(t);
  }, [tagFilterInput, tagFilter]);

  // Debounce phone filter
  useEffect(() => {
    const value = phoneFilterInput.trim();
    if (value === phoneFilter) return;
    const t = setTimeout(() => {
      setPhoneFilter(value);
      setConversationsPage(1);
    }, 700);
    return () => clearTimeout(t);
  }, [phoneFilterInput, phoneFilter]);

  // Native browser prompt on refresh/close/tab switch while envío masivo is running
  useEffect(() => {
    if (!bulkTemplateLoading) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [bulkTemplateLoading]);

  // Current page conversations (what backend returned)
  const filteredConversations = conversations;

  // Chart data: merge conversations and sessions by date (apply date filter to see trend)
  const conversationsPerDayChartData = React.useMemo(() => {
    const byDate = {};
    conversationsPerDayFromBackend.forEach(({ date, count }) => {
      byDate[date] = { date, count: count || 0, tagCount: 0, sessionsCount: 0 };
    });
    tagConversationsPerDayFromBackend.forEach(({ date, count }) => {
      if (!byDate[date]) byDate[date] = { date, count: 0, tagCount: 0, sessionsCount: 0 };
      byDate[date].tagCount = count || 0;
    });
    sessionsPerDayFromBackend.forEach(({ date, count }) => {
      if (!byDate[date]) byDate[date] = { date, count: 0, tagCount: 0, sessionsCount: 0 };
      byDate[date].sessionsCount = count || 0;
    });
    const dates = Object.keys(byDate).sort();
    if (!dates.length) return [];
    // Parse as UTC so axis and tooltip show the same calendar day in all timezones
    const formatChartDate = (dateStr, options = {}) => {
      const d = new Date(dateStr + "T00:00:00.000Z");
      return d.toLocaleDateString("es-ES", { timeZone: "UTC", ...options });
    };
    return dates.map((date) => ({
      ...byDate[date],
      dateLabel: formatChartDate(date, { day: "2-digit", month: "short" }),
    }));
  }, [conversationsPerDayFromBackend, tagConversationsPerDayFromBackend, sessionsPerDayFromBackend]);

  // AI Analysis (Analizar con IA) - same as mantis-front Conversations
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisConfig, setAnalysisConfig] = useState({
    model: "us.anthropic.claude-haiku-4-5-20251001-v1:0",
    prompt: "",
    outputColumns: [
      { key: "conversationId", label: "ID de Conversación", enabled: true },
      { key: "contactName", label: "Nombre del Contacto", enabled: true },
      { key: "contactEmail", label: "Email del Contacto", enabled: true },
      { key: "contactPhone", label: "Teléfono del Contacto", enabled: true },
      { key: "contactCompany", label: "Empresa del Contacto", enabled: true },
      { key: "status", label: "Estado", enabled: true },
      { key: "priority", label: "Prioridad", enabled: true },
      { key: "assignedAgent", label: "Agente Asignado", enabled: true },
      { key: "analysisData", label: "Datos del Análisis (JSON)", enabled: true },
      { key: "totalMessages", label: "Total de Mensajes", enabled: true },
      { key: "analysisDate", label: "Fecha de Análisis", enabled: true },
      { key: "dateCreated", label: "Fecha de Creación", enabled: true },
    ],
  });

  const performAnalysis = React.useCallback(
    async (conversation, messages) => {
      return await analyzeConversation(
        conversation,
        messages,
        analysisConfig.prompt,
        { model: analysisConfig.model }
      );
    },
    [analysisConfig.prompt, analysisConfig.model]
  );

  const getMessagesForConversation = React.useCallback(
    async (conversationId) => {
      try {
        const messagesData = await getMantisConversationMessagesByConversationId(
          token,
          conversationId,
          1000,
          0
        );
        return messagesData?.data || messagesData || [];
      } catch (err) {
        console.error(`Error fetching messages for conversation ${conversationId}:`, err);
        return [];
      }
    },
    [token]
  );

  const {
    processBatch,
    isProcessing: isAnalyzing,
    progress: analysisProgress,
    results: batchResults,
    errors: batchErrors,
    isPaused: isAnalysisPaused,
    pause: pauseAnalysis,
    resume: resumeAnalysis,
    cancel: cancelAnalysis,
    reset: resetAnalysis,
  } = useBatchAIAnalysis(performAnalysis, {
    batchSize: 1,
    delayBetweenBatches: 2000,
    onComplete: (results, errors) => {
      console.log("Analysis complete:", results.length, "successful,", errors.length, "errors");
    },
  });

  const startAnalysis = async () => {
    if (filteredConversations.length === 0) {
      alert("No hay conversaciones para analizar (aplica un filtro de fechas o asegúrate de tener registros)");
      return;
    }
    if (!analysisConfig.prompt.trim()) {
      alert("Por favor, configura el prompt antes de iniciar el análisis");
      setShowConfigModal(true);
      return;
    }
    setShowConfigModal(false);
    setShowAnalysisModal(true);
    resetAnalysis();
    try {
      await processBatch(filteredConversations, getMessagesForConversation);
    } catch (err) {
      console.error("Error in batch analysis:", err);
      alert("Error al iniciar el análisis. Por favor, intenta de nuevo.");
    }
  };

  const handleColumnToggle = (index) => {
    const newColumns = [...analysisConfig.outputColumns];
    newColumns[index].enabled = !newColumns[index].enabled;
    setAnalysisConfig({ ...analysisConfig, outputColumns: newColumns });
  };

  const handleAddCustomColumn = () => {
    const label = prompt("Ingresa el nombre de la columna:");
    if (label?.trim()) {
      const key = prompt(
        "Ingresa la clave (key) para acceder al dato del análisis (ej: categoria, explicacion):"
      );
      if (key?.trim()) {
        setAnalysisConfig({
          ...analysisConfig,
          outputColumns: [
            ...analysisConfig.outputColumns,
            { key: key.trim(), label: label.trim(), enabled: true },
          ],
        });
      }
    }
  };

  const handleRemoveCustomColumn = (index) => {
    const newColumns = analysisConfig.outputColumns.filter((_, i) => i !== index);
    setAnalysisConfig({ ...analysisConfig, outputColumns: newColumns });
  };

  const exportAnalysisToExcel = async (resultsToExport = null) => {
    const results = resultsToExport || batchResults || [];
    if (results.length === 0) {
      alert("No hay resultados para exportar");
      return;
    }
    try {
      const wb = XLSX.utils.book_new();
      const enabledColumns = analysisConfig.outputColumns.filter((c) => c.enabled);
      const includeAnalysisDataColumn = enabledColumns.some((c) => c.key === "analysisData");
      const standardColumns = enabledColumns.filter((c) => c.key !== "analysisData");

      const analysisKeysSet = new Set();
      results.forEach((result) => {
        const analysis = result.analysis?.data || result.analysis || {};
        if (analysis && typeof analysis === "object" && !Array.isArray(analysis)) {
          Object.keys(analysis).forEach((k) => analysisKeysSet.add(k));
        }
      });
      const analysisKeys = Array.from(analysisKeysSet);
      const analysisColumns = analysisKeys.map((key) => ({
        key,
        label: key.charAt(0).toUpperCase() + key.slice(1),
        enabled: true,
      }));
      const allColumns = [...standardColumns, ...analysisColumns];
      if (includeAnalysisDataColumn) {
        const col = enabledColumns.find((c) => c.key === "analysisData");
        if (col) allColumns.push(col);
      }

      const analysisData = results.map((result) => {
        const conv = result.conversation;
        const analysis = result.analysis?.data || result.analysis || {};
        const row = {};
        standardColumns.forEach((col) => {
          switch (col.key) {
            case "conversationId":
              row[col.label] = result.conversationId;
              break;
            case "contactName":
              row[col.label] = conv.contact?.name || conv.contactName || "Sin nombre";
              break;
            case "contactEmail":
              row[col.label] = conv.contact?.email || conv.contactEmail || "N/A";
              break;
            case "contactPhone":
              row[col.label] = conv.contact?.phoneNumber || conv.contactPhone || "N/A";
              break;
            case "contactCompany":
              row[col.label] = conv.contact?.company || conv.contactCompany || "N/A";
              break;
            case "status":
              row[col.label] = conv.status || "N/A";
              break;
            case "priority":
              row[col.label] = conv.priority || "NORMAL";
              break;
            case "assignedAgent":
              row[col.label] = conv.assignedAgentId?.name || "Sin asignar";
              break;
            case "totalMessages":
              row[col.label] = result.messages?.length || 0;
              break;
            case "analysisDate":
              row[col.label] = result.processedAt ? formatDate(result.processedAt) : "N/A";
              break;
            case "dateCreated":
              row[col.label] = conv.dateCreated ? formatDate(conv.dateCreated) : "N/A";
              break;
            default:
              row[col.label] = analysis[col.key] ?? "N/A";
          }
        });
        analysisKeys.forEach((key) => {
          const value = analysis[key];
          const label = key.charAt(0).toUpperCase() + key.slice(1);
          if (value == null) row[label] = "N/A";
          else if (typeof value === "object") row[label] = JSON.stringify(value);
          else row[label] = String(value);
        });
        if (includeAnalysisDataColumn) {
          const col = enabledColumns.find((c) => c.key === "analysisData");
          if (col) row[col.label] = JSON.stringify(analysis);
        }
        return row;
      });

      const ws = XLSX.utils.json_to_sheet(analysisData);
      const colWidths = allColumns.map((col) => {
        if (col.key === "contactEmail") return { wch: 30 };
        if (col.key === "conversationId") return { wch: 25 };
        if (col.key === "analysisData") return { wch: 50 };
        if (analysisKeys.includes(col.key)) return { wch: 20 };
        return { wch: 20 };
      });
      ws["!cols"] = colWidths;
      XLSX.utils.book_append_sheet(wb, ws, "Análisis de Conversaciones");

      const summaryData = [
        { Métrica: "Total Analizado", Valor: results.length },
        { Métrica: "Exitosos", Valor: results.filter((r) => r.success).length },
        { Métrica: "Con Errores", Valor: batchErrors.length },
      ];
      const summaryWs = XLSX.utils.json_to_sheet(summaryData);
      summaryWs["!cols"] = [{ wch: 30 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, summaryWs, "Resumen");

      const filename = `analisis_conversaciones_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, filename);
      alert(`Análisis exportado exitosamente: ${filename}`);
    } catch (err) {
      console.error("Error exporting analysis:", err);
      alert(`Error al exportar: ${err.message}`);
    }
  };

  // L-01 (MA-06): do NOT gate this on `token`. The JWT moved into an httpOnly cookie,
  // so Redux holds "" on reload and undefined after login — a falsy token no longer
  // means "not authenticated", it means nothing at all. Gating on it stopped both
  // fetches from ever running, and since `loading` starts true and is only cleared
  // inside them, the tab spun forever. `mantisBusinessId` is the real readiness signal:
  // it only lands once verifySession has populated the business.
  useEffect(() => {
    if (mantisBusinessId && activeTab === "agentes") {
      fetchAgents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, mantisBusinessId, activeTab]);

  useEffect(() => {
    if (mantisBusinessId && activeTab === "conversations") {
      fetchConversations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, mantisBusinessId, activeTab, conversationsPage, pageSize, dateFilterFrom, dateFilterTo, tagFilter, phoneFilter]);

  // When no business context, clear data and stop loading for agents
  useEffect(() => {
    if (!mantisBusinessId && activeTab === "agentes") {
      setLoading(false);
      setAgents([]);
    }
  }, [mantisBusinessId, activeTab]);

  const fetchAgents = async () => {
    if (!mantisBusinessId) {
      setAgents([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await getMantisAgentsWithDecryptedDataByBusinessId(
        token,
        mantisBusinessId
      );
      setAgents(data.data || []);
    } catch (err) {
      setError("Error al cargar los agentes");
      console.error("Error fetching agents:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async () => {
    if (!mantisBusinessId) return;
    try {
      setLoading(true);
      setError(null);
      const options = {
        page: conversationsPage,
        limit: pageSize,
        ...(dateFilterFrom && { dateFrom: dateFilterFrom }),
        ...(dateFilterTo && { dateTo: dateFilterTo }),
        ...(tagFilter && { tag: tagFilter }),
        ...(phoneFilter && { phone: phoneFilter }),
      };
      const res = await getMantisConversationsByBusinessId(token, mantisBusinessId, options);
      const list = Array.isArray(res.data) ? res.data : [];
      setConversations(list);
      setConversationsTotal(
        typeof res.total === "number" ? res.total : list.length
      );
      setConversationsPerDayFromBackend(
        Array.isArray(res.conversationsPerDay) ? res.conversationsPerDay : []
      );
      setSessionsPerDayFromBackend(
        Array.isArray(res.sessionsPerDay) ? res.sessionsPerDay : []
      );
      setTagConversationsPerDayFromBackend(
        Array.isArray(res.tagConversationsPerDay) ? res.tagConversationsPerDay : []
      );
    } catch (err) {
      setError("Error al cargar las conversaciones");
      console.error("Error fetching conversations:", err);
    } finally {
      setLoading(false);
    }
  };

  const EXCEL_MAX_CELL_LENGTH = 32767;
  const truncateForExcel = (text, suffix = "") => {
    if (!text || typeof text !== "string") return text || "";
    if (text.length <= EXCEL_MAX_CELL_LENGTH) return text;
    const maxLength = EXCEL_MAX_CELL_LENGTH - suffix.length;
    return text.substring(0, maxLength) + suffix;
  };

  const getContextKeys = (list) => {
    const keySet = new Set();
    (list || []).forEach((c) => {
      const ctx = c.context;
      if (ctx && typeof ctx === "object") Object.keys(ctx).forEach((k) => keySet.add(k));
    });
    return [...keySet].sort();
  };

  const formatContextValue = (val) => {
    if (val === undefined || val === null) return "N/A";
    if (Array.isArray(val)) {
      const str = val.every((x) => typeof x !== "object" || x === null)
        ? val.map(String).join(", ")
        : JSON.stringify(val);
      return truncateForExcel(str);
    }
    if (typeof val === "object") return truncateForExcel(JSON.stringify(val));
    return String(val);
  };

  const exportConversationsToExcel = async () => {
    if (conversationsTotal === 0) {
      alert("No hay conversaciones para exportar (aplica un filtro de fechas o asegúrate de tener registros)");
      return;
    }
    setExportLoading(true);
    try {
      const exportOptions = {
        limit: 200,
        ...(dateFilterFrom && { dateFrom: dateFilterFrom }),
        ...(dateFilterTo && { dateTo: dateFilterTo }),
        ...(tagFilter && { tag: tagFilter }),
        ...(phoneFilter && { phone: phoneFilter }),
      };
      const allConversations = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const res = await getMantisConversationsByBusinessId(token, mantisBusinessId, {
          ...exportOptions,
          page,
        });
        const list = Array.isArray(res.data) ? res.data : [];
        allConversations.push(...list);
        hasMore = list.length === 200 && (res.total == null || allConversations.length < res.total);
        page++;
      }

      if (allConversations.length === 0) {
        alert("No se encontraron conversaciones para exportar");
        setExportLoading(false);
        return;
      }

      const wb = XLSX.utils.book_new();
      const contactName = (c) => c.contact?.name ?? c.contactName ?? "Sin nombre";
      const contactEmail = (c) => c.contact?.email ?? c.contactEmail ?? "N/A";
      const contactPhone = (c) => c.contact?.phoneNumber ?? c.contactPhone ?? "N/A";
      const contactCompany = (c) => c.contact?.company ?? c.contactCompany ?? "N/A";
      const assignedAgentName = (c) => c.assignedAgentId?.name ?? "Sin asignar";
      const assignedAgentId = (c) => c.assignedAgentId?.agentId ?? "N/A";

      const contextKeys = getContextKeys(allConversations);

      const conversationsData = allConversations.map((c) => {
        const base = {
          "ID de Conversación": c._id,
          "Nombre del Contacto": contactName(c),
          "Email del Contacto": contactEmail(c),
          "Teléfono del Contacto": contactPhone(c),
          "Empresa del Contacto": contactCompany(c),
          Estado: c.status ?? "N/A",
          Prioridad: c.priority ?? "NORMAL",
          "Agente Asignado": assignedAgentName(c),
          "ID del Agente": assignedAgentId(c),
          Canales: c.channels?.map((ch) => ch.type ?? ch).join(", ") ?? "N/A",
          "Fecha de Creación": c.dateCreated ? formatDate(c.dateCreated) : "N/A",
          "Fecha de Actualización": c.dateUpdated ? formatDate(c.dateUpdated) : "N/A",
          Tags: truncateForExcel(c.tags?.join(", ") ?? "N/A"),
          "ID de Negocio": c.mantisBusinessId ?? "N/A",
          "ID de Usuario": c.userId ?? "N/A",
        };
        const ctx = c.context || {};
        contextKeys.forEach((k) => {
          base[`Context - ${k}`] = formatContextValue(ctx[k]);
        });
        return base;
      });
      const conversationsWs = XLSX.utils.json_to_sheet(conversationsData);
      const conversationsColWidths = [
        { wch: 25 }, { wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 25 },
        { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 20 }, { wch: 30 },
        { wch: 20 }, { wch: 20 }, { wch: 30 }, { wch: 20 }, { wch: 20 },
        ...contextKeys.map(() => ({ wch: 25 })),
      ];
      conversationsWs["!cols"] = conversationsColWidths;
      XLSX.utils.book_append_sheet(wb, conversationsWs, "Conversaciones");

      const conversationMessagesData = [];
      const messagePromises = allConversations.map(async (conversation) => {
        try {
          const messagesData = await getMantisConversationMessagesByConversationId(
            token,
            conversation._id,
            1000,
            0
          );
          const messages = messagesData?.data ?? messagesData ?? [];
          return {
            conversationId: conversation._id,
            conversationName: contactName(conversation),
            messages,
          };
        } catch (err) {
          console.error(`Error fetching messages for conversation ${conversation._id}:`, err);
          return {
            conversationId: conversation._id,
            conversationName: contactName(conversation),
            messages: [],
          };
        }
      });
      const results = await Promise.all(messagePromises);

      results.forEach(({ conversationId, conversationName, messages }) => {
        const sortedMessages = (messages || []).sort((a, b) => {
          const dateA = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
          const dateB = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
          return dateA - dateB;
        });
        let conversationText = sortedMessages
          .map((msg, index) => {
            const sender =
              msg.sender === "USER" ? "Usuario"
              : msg.sender === "AGENT" ? "Agente"
              : msg.sender === "MANTIS" ? "Mantis"
              : msg.sender ?? "Desconocido";
            const timestamp = msg.dateCreated ? formatDate(msg.dateCreated) : "Sin fecha";
            let content = msg.content?.text ?? msg.content?.html ?? "Sin contenido";
            const messageHeader = `${index + 1}. [${sender}] (${timestamp}): `;
            const maxContentLength = EXCEL_MAX_CELL_LENGTH - messageHeader.length - 50;
            if (content.length > maxContentLength) {
              content = content.substring(0, maxContentLength) + "... [contenido truncado]";
            }
            return `${messageHeader}${content}`;
          })
          .join("\n\n");
        const truncationNote = "\n\n[... La conversación fue truncada debido a la longitud máxima de Excel (32767 caracteres) ...]";
        conversationText = truncateForExcel(conversationText || "Sin mensajes", truncationNote);
        const conv = allConversations.find((c) => c._id === conversationId);
        const ctx = conv?.context || {};
        const msgRow = {
          "ID de Conversación": conversationId,
          "Nombre de Conversación": conversationName,
          "Email del Contacto": contactEmail(conv),
          "Teléfono del Contacto": contactPhone(conv),
          "Empresa del Contacto": contactCompany(conv),
          "Estado de Conversación": conv?.status ?? "N/A",
          Prioridad: conv?.priority ?? "NORMAL",
          "Agente Asignado": assignedAgentName(conv),
          Canales: conv?.channels?.map((ch) => ch.type ?? ch).join(", ") ?? "N/A",
          "Conversación Completa": conversationText || "Sin mensajes",
          "Total de Mensajes": sortedMessages.length,
          "Primer Mensaje": sortedMessages.length > 0 ? formatDate(sortedMessages[0].dateCreated) : "N/A",
          "Último Mensaje": sortedMessages.length > 0 ? formatDate(sortedMessages[sortedMessages.length - 1].dateCreated) : "N/A",
          "Fecha de Creación": conv?.dateCreated ? formatDate(conv.dateCreated) : "N/A",
          "Fecha de Actualización": conv?.dateUpdated ? formatDate(conv.dateUpdated) : "N/A",
        };
        contextKeys.forEach((k) => {
          msgRow[`Context - ${k}`] = formatContextValue(ctx[k]);
        });
        conversationMessagesData.push(msgRow);
      });

      if (conversationMessagesData.length > 0) {
        const messagesWs = XLSX.utils.json_to_sheet(conversationMessagesData);
        const messagesColWidths = [
          { wch: 25 }, { wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 25 },
          { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 30 }, { wch: 100 },
          { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
          ...contextKeys.map(() => ({ wch: 25 })),
        ];
        messagesWs["!cols"] = messagesColWidths;
        XLSX.utils.book_append_sheet(wb, messagesWs, "Mensajes");
      } else {
        const emptyRow = {
          "ID de Conversación": "N/A", "Nombre de Conversación": "Sin datos",
          "Email del Contacto": "N/A", "Teléfono del Contacto": "N/A", "Empresa del Contacto": "N/A",
          "Estado de Conversación": "N/A", Prioridad: "N/A", "Agente Asignado": "N/A", Canales: "N/A",
          "Conversación Completa": "No se pudieron cargar los mensajes", "Total de Mensajes": 0,
          "Primer Mensaje": "N/A", "Último Mensaje": "N/A", "Fecha de Creación": "N/A", "Fecha de Actualización": "N/A",
        };
        contextKeys.forEach((k) => { emptyRow[`Context - ${k}`] = "N/A"; });
        const emptyMessagesData = [emptyRow];
        const emptyMessagesWs = XLSX.utils.json_to_sheet(emptyMessagesData);
        XLSX.utils.book_append_sheet(wb, emptyMessagesWs, "Mensajes");
      }

      const totalConversations = allConversations.length;
      const summaryData = [
        { Métrica: "Total de Conversaciones", Valor: totalConversations },
        { Métrica: "Conversaciones Activas", Valor: allConversations.filter((c) => c.status === "ACTIVE").length },
        { Métrica: "Conversaciones Pausadas", Valor: allConversations.filter((c) => c.status === "PAUSED").length },
        { Métrica: "Conversaciones Cerradas", Valor: allConversations.filter((c) => c.status === "CLOSED").length },
        { Métrica: "Conversaciones Archivadas", Valor: allConversations.filter((c) => c.status === "ARCHIVED").length },
        { Métrica: "", Valor: "" },
        { Métrica: "Prioridad Baja", Valor: allConversations.filter((c) => c.priority === "LOW").length },
        { Métrica: "Prioridad Normal", Valor: allConversations.filter((c) => c.priority === "NORMAL").length },
        { Métrica: "Prioridad Alta", Valor: allConversations.filter((c) => c.priority === "HIGH").length },
        { Métrica: "Prioridad Urgente", Valor: allConversations.filter((c) => c.priority === "URGENT").length },
        { Métrica: "", Valor: "" },
        { Métrica: "Conversaciones con Agente Asignado", Valor: allConversations.filter((c) => c.assignedAgentId).length },
        { Métrica: "Conversaciones sin Agente Asignado", Valor: allConversations.filter((c) => !c.assignedAgentId).length },
      ];
      const channelCounts = {};
      allConversations.forEach((c) => {
        (c.channels || []).forEach((ch) => {
          const type = ch.type ?? ch;
          channelCounts[type] = (channelCounts[type] || 0) + 1;
        });
      });
      Object.entries(channelCounts).forEach(([channel, count]) => {
        summaryData.push({ Métrica: `Canal ${channel}`, Valor: count });
      });
      const totalMessages = conversationMessagesData.length > 0
        ? conversationMessagesData.reduce((sum, conv) => sum + (conv["Total de Mensajes"] || 0), 0)
        : 0;
      summaryData.push({ Métrica: "", Valor: "" }, { Métrica: "Total de Mensajes", Valor: totalMessages });
      const conversationsWithMessages = conversationMessagesData.filter((conv) => (conv["Total de Mensajes"] || 0) > 0).length;
      summaryData.push(
        { Métrica: "Conversaciones con Mensajes", Valor: conversationsWithMessages },
        { Métrica: "Conversaciones sin Mensajes", Valor: conversationMessagesData.length > 0 ? conversationMessagesData.filter((c) => (c["Total de Mensajes"] || 0) === 0).length : allConversations.length },
        { Métrica: "Promedio de Mensajes por Conversación", Valor: conversationsWithMessages > 0 ? (totalMessages / conversationsWithMessages).toFixed(2) : 0 }
      );
      const summaryWs = XLSX.utils.json_to_sheet(summaryData);
      summaryWs["!cols"] = [{ wch: 30 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, summaryWs, "Resumen");

      if (!wb.SheetNames || wb.SheetNames.length === 0) {
        throw new Error("El libro de trabajo no tiene hojas. No se puede exportar.");
      }
      const currentDate = new Date().toISOString().split("T")[0];
      const filename = `conversaciones_mantis_${currentDate}.xlsx`;
      XLSX.writeFile(wb, filename);
      alert(`Archivo exportado exitosamente: ${filename}`);
    } catch (err) {
      console.error("Error exporting conversations:", err);
      alert(`Error al exportar las conversaciones: ${err.message || "Error desconocido"}. Por favor, verifica la consola para más detalles.`);
    } finally {
      setExportLoading(false);
    }
  };

  const openConversationDetail = async (conversation) => {
    setSelectedConversation(conversation);
    setConversationModal(true);
    setDetailsLoading(true);
    setConversationMessages([]);
    setConversationSessions([]);
    try {
      const [messagesRes, sessionsRes] = await Promise.all([
        getMantisConversationMessagesByConversationId(token, conversation._id, 100, 0),
        getMantisConversationSessionsByConversationId(token, conversation._id),
      ]);
      setConversationMessages(messagesRes?.data || messagesRes || []);
      setConversationSessions(sessionsRes?.data || sessionsRes || []);
    } catch (err) {
      console.error("Error fetching conversation details:", err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeConversationDetail = () => {
    setConversationModal(false);
    setSelectedConversation(null);
    setConversationMessages([]);
    setConversationSessions([]);
  };

  const scrollToLastMessage = () => {
    if (messagesScrollRef.current) {
      messagesScrollRef.current.scrollTo({
        top: messagesScrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const checkScrollPosition = () => {
    const el = messagesScrollRef.current;
    if (!el) return;
    const threshold = 20;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
    setIsScrolledToBottom(atBottom);
  };

  useEffect(() => {
    if (!conversationModal || !selectedConversation) return;
    setIsScrolledToBottom(false);
  }, [conversationModal, selectedConversation]);

  useEffect(() => {
    if (conversationMessages.length === 0) return;
    const t = setTimeout(checkScrollPosition, 150);
    return () => clearTimeout(t);
  }, [conversationMessages]);

  const processExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          const recipients = [];
          if (jsonData.length < 3) {
            throw new Error(
              "El archivo debe tener al menos 3 filas: encabezados, tipos y datos"
            );
          }

          const headers = jsonData[0];
          const typeRow = jsonData[1];
          const phoneColumnIndex = typeRow.findIndex(
            (type) =>
              type === "PARAMETER" &&
              headers[typeRow.indexOf(type)]?.toLowerCase().includes("telefono")
          );
          if (phoneColumnIndex === -1) {
            throw new Error(
              "No se encontró una columna de teléfono marcada como PARAMETER"
            );
          }

          for (let i = 2; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (row && row.length > 0 && row[phoneColumnIndex]) {
              const phoneNumber = row[phoneColumnIndex].toString().trim();
              if (phoneNumber) {
                const parameters = [];
                const context = {};
                for (let j = 0; j < row.length; j++) {
                  if (
                    row[j] !== undefined &&
                    row[j] !== null &&
                    row[j] !== ""
                  ) {
                    const header = headers[j] || `Column_${j}`;
                    const type = typeRow[j];
                    const value = row[j].toString().trim();
                    if (type === "PARAMETER" && j !== phoneColumnIndex) {
                      parameters.push(value);
                    } else if (type === "CONTEXT") {
                      context[header] = value;
                    }
                  }
                }
                recipients.push({ phoneNumber, parameters, context });
              }
            }
          }
          resolve(recipients);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error("Error reading file"));
      reader.readAsArrayBuffer(file);
    });
  };

  const downloadTemplate = () => {
    const templateData = [
      ["Telefono", "Nombre", "Email", "Empresa", "Producto", "Precio", "Fecha_Limite", "Notas_Adicionales"],
      ["PHONE", "PARAMETER", "CONTEXT", "CONTEXT", "PARAMETER", "PARAMETER", "PARAMETER", "CONTEXT"],
      ["1234567890", "Juan Pérez", "juan@empresa.com", "Empresa ABC", "Producto X", "$100", "2024-12-31", "Cliente VIP"],
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "template_bulk_whatsapp.xlsx");
  };

  const openBulkTemplateModal = (agent, whatsappAccount = null) => {
    if (bulkTemplateLoading) {
      setBulkTemplateModal(true);
      return;
    }
    setBulkTemplateAgent(agent);
    setSelectedWhatsAppAccount(
      whatsappAccount ||
        (agent.whatsappAccounts?.length === 1 ? agent.whatsappAccounts[0] : null)
    );
    setBulkTemplateForm({
      templateName: "",
      excelFile: null,
      language: "es_MX",
    });
    setBulkTemplateError(null);
    setBulkTemplateSuccess(null);
    setBulkTemplateResults(null);
    setShowDetailedResults(false);
    setBulkTemplateCompletionBannerDismissed(false);
    setBulkTemplateProgress({
      currentBatch: 0,
      totalBatches: 0,
      currentSent: 0,
      currentFailed: 0,
      totalRecipients: 0,
    });
    setBulkTemplateModal(true);
  };

  const resetBulkTemplateSession = () => {
    setSelectedWhatsAppAccount(null);
    setBulkTemplateAgent(null);
    setBulkTemplateForm({
      templateName: "",
      excelFile: null,
      language: "es_MX",
    });
    setBulkTemplateError(null);
    setBulkTemplateSuccess(null);
    setBulkTemplateResults(null);
    setShowDetailedResults(false);
    setBulkTemplateCompletionBannerDismissed(false);
    setBulkTemplateProgress({
      currentBatch: 0,
      totalBatches: 0,
      currentSent: 0,
      currentFailed: 0,
      totalRecipients: 0,
    });
  };

  const closeBulkTemplateModal = () => {
    setBulkTemplateModal(false);
    if (!bulkTemplateLoading) {
      resetBulkTemplateSession();
    }
  };

  const handleBulkTemplateFormChange = (field, value) => {
    setBulkTemplateForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleBulkTemplateSubmit = async () => {
    if (!bulkTemplateAgent || !selectedWhatsAppAccount) {
      setBulkTemplateError("Selecciona una cuenta de WhatsApp");
      return;
    }
    try {
      setBulkTemplateLoading(true);
      setBulkTemplateCompletionBannerDismissed(false);
      setBulkTemplateError(null);
      setBulkTemplateSuccess(null);
      setBulkTemplateResults(null);
      setShowDetailedResults(false);

      if (!bulkTemplateForm.templateName.trim()) {
        setBulkTemplateError("El nombre del template es requerido");
        setBulkTemplateLoading(false);
        return;
      }
      if (!bulkTemplateForm.excelFile) {
        setBulkTemplateError("El archivo Excel es requerido");
        setBulkTemplateLoading(false);
        return;
      }

      const excelData = await processExcelFile(bulkTemplateForm.excelFile);
      if (!excelData || excelData.length === 0) {
        setBulkTemplateError("No se encontraron datos válidos en el archivo Excel");
        setBulkTemplateLoading(false);
        return;
      }

      const batchSize = 10;
      const totalBatches = Math.ceil(excelData.length / batchSize);
      setBulkTemplateProgress({
        currentBatch: 0,
        totalBatches,
        currentSent: 0,
        currentFailed: 0,
        totalRecipients: excelData.length,
      });

      const result = await sendBulkTemplates({
        agentId: bulkTemplateAgent._id,
        whatsappAccount: selectedWhatsAppAccount,
        templateName: bulkTemplateForm.templateName,
        language: bulkTemplateForm.language,
        recipients: excelData,
        token,
        batchSize,
        onProgress: (currentBatch, totalBatches, currentSent, currentFailed) => {
          setBulkTemplateProgress({
            currentBatch,
            totalBatches,
            currentSent,
            currentFailed,
            totalRecipients: excelData.length,
          });
        },
      });

      if (result.success) {
        const successMessage = `Se enviaron ${result.sent} templates exitosamente. ${result.failed} fallaron.`;
        setBulkTemplateSuccess(successMessage);
        setBulkTemplateResults({
          sent: result.sent,
          failed: result.failed,
          total: result.total,
          successfulPhoneNumbers: result.successfulPhoneNumbers || [],
          failedPhoneNumbers: result.failedPhoneNumbers || [],
          errors: result.errors || [],
        });
      } else {
        setBulkTemplateError(result.error || "Error al enviar los templates");
        setBulkTemplateResults(null);
      }
    } catch (err) {
      setBulkTemplateError("Error al procesar el envío masivo: " + err.message);
      console.error("Error sending bulk templates:", err);
    } finally {
      setBulkTemplateLoading(false);
    }
  };

  return (
    <div className="p-4">
      <style>{`
        .bulk-template-progress.progress .progress-bar {
          height: 100%;
        }
        .analysis-progress.progress {
          overflow: hidden;
          background-color: #e9ecef;
          display: flex;
        }
        .analysis-progress.progress .progress-bar {
          height: 100%;
          min-width: 2%;
          background-color: #0d6efd;
          transition: width 0.3s ease;
        }
        .agents-section-tabs.nav-tabs .nav-link:not(.active) {
          color: #f97316;
        }
        .agents-section-tabs.nav-tabs .nav-link:not(.active):hover {
          color: #ea580c;
        }
      `}</style>
      <h2 className="mb-4 text-xl font-semibold text-slate-800 dark:text-slate-200">
        Agentes Mantis
      </h2>
      <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
        Visualización de agentes y conversaciones de tu negocio.
      </p>

      {bulkTemplateLoading && !bulkTemplateModal && (
        <Alert
          color="primary"
          className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3"
        >
          <div className="d-flex align-items-center gap-2">
            <Spinner size="sm" />
            <span>
              Envío masivo en curso
              {bulkTemplateProgress.totalBatches > 0 && (
                <>
                  {" "}
                  (lote {bulkTemplateProgress.currentBatch} /{" "}
                  {bulkTemplateProgress.totalBatches},{" "}
                  {bulkTemplateProgress.currentSent +
                    bulkTemplateProgress.currentFailed}{" "}
                  / {bulkTemplateProgress.totalRecipients} destinatarios)
                </>
              )}
            </span>
          </div>
          <Button
            color="light"
            size="sm"
            onClick={() => setBulkTemplateModal(true)}
          >
            Ver progreso
          </Button>
        </Alert>
      )}

      {!bulkTemplateLoading &&
        !bulkTemplateModal &&
        (bulkTemplateSuccess || bulkTemplateError) &&
        !bulkTemplateCompletionBannerDismissed && (
          <Alert
            color={bulkTemplateError ? "danger" : "success"}
            className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3"
            toggle={() => setBulkTemplateCompletionBannerDismissed(true)}
          >
            <span>{bulkTemplateError || bulkTemplateSuccess}</span>
            <Button
              color={bulkTemplateError ? "outline-light" : "outline-dark"}
              size="sm"
              onClick={() => setBulkTemplateModal(true)}
            >
              Ver detalles
            </Button>
          </Alert>
        )}

      <Nav tabs className="mb-3 agents-section-tabs">
        <NavItem>
          <NavLink
            className={activeTab === "agentes" ? "active" : ""}
            onClick={() => setActiveTab("agentes")}
            style={{ cursor: "pointer" }}
          >
            Agentes
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={activeTab === "conversations" ? "active" : ""}
            onClick={() => setActiveTab("conversations")}
            style={{ cursor: "pointer" }}
          >
            Conversaciones
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={activeTab}>
        <TabPane tabId="agentes">
          <Card>
            <CardBody>
              {loading ? (
                <div className="d-flex justify-content-center py-5">
                  <Spinner />
                </div>
              ) : error ? (
                <div className="alert alert-danger">{error}</div>
              ) : (
                <div className="table-responsive">
                  <Table size="sm" hover>
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Tipo</th>
                        <th>Estado despliegue</th>
                        <th>Descripción</th>
                        {showBulkTemplates && <th>Acciones</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {agents.length === 0 ? (
                        <tr>
                          <td
                            colSpan={showBulkTemplates ? 5 : 4}
                            className="text-center text-muted py-4"
                          >
                            No hay agentes
                          </td>
                        </tr>
                      ) : (
                        agents.map((agent) => (
                          <tr key={agent._id}>
                            <td>{agent.name || "—"}</td>
                            <td>{getAgentTypeLabel(agent.type)}</td>
                            <td>
                              {agent.deploymentStatus ? (
                                <Badge
                                  color={
                                    agent.deploymentStatus === "completed"
                                      ? "success"
                                      : agent.deploymentStatus === "failed"
                                        ? "danger"
                                        : "warning"
                                  }
                                >
                                  {agent.deploymentStatus === "completed"
                                    ? "Completado"
                                    : agent.deploymentStatus === "failed"
                                      ? "Fallido"
                                      : agent.deploymentStatus === "in_progress"
                                        ? "En progreso"
                                        : "Pendiente"}
                                </Badge>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td style={{ maxWidth: 200 }}>
                              {agent.description
                                ? agent.description.length > 60
                                  ? `${agent.description.slice(0, 60)}...`
                                  : agent.description
                                : "—"}
                            </td>
                            {showBulkTemplates && (
                              <td>
                                {agent.whatsappAccounts?.length > 0 ? (
                                  agent.whatsappAccounts.length === 1 ? (
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() =>
                                        openBulkTemplateModal(
                                          agent,
                                          agent.whatsappAccounts[0],
                                        )
                                      }
                                    >
                                      <i className="fas fa-paper-plane me-1" />
                                      Envío masivo
                                    </Button>
                                  ) : (
                                    <Input
                                      type="select"
                                      size="sm"
                                      style={{ width: "auto", minWidth: 140 }}
                                      onChange={(e) => {
                                        const idx = e.target.value;
                                        if (idx !== "") {
                                          openBulkTemplateModal(
                                            agent,
                                            agent.whatsappAccounts[
                                              parseInt(idx, 10)
                                            ],
                                          );
                                        }
                                      }}
                                    >
                                      <option value="">Envío masivo...</option>
                                      {agent.whatsappAccounts.map((acc, idx) => (
                                        <option key={idx} value={idx}>
                                          {acc.accountName ||
                                            acc.phoneId ||
                                            `Cuenta ${idx + 1}`}
                                        </option>
                                      ))}
                                    </Input>
                                  )
                                ) : (
                                  <span className="text-muted small">—</span>
                                )}
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </TabPane>

        <TabPane tabId="conversations">
          <Card>
            <CardBody>
              {loading ? (
                <div className="d-flex justify-content-center py-5">
                  <Spinner />
                </div>
              ) : error ? (
                <div className="alert alert-danger">{error}</div>
              ) : (
                <>
                  <Row className="mb-3 align-items-end">
                    <Col xs={12} md="auto">
                      <Label className="me-2 mb-0" style={{ color: "#212529" }}>
                        Desde
                      </Label>
                      <label
                        htmlFor="date-filter-from"
                        className="d-flex align-items-center gap-2 border rounded px-2 py-1"
                        style={{
                          minWidth: 140,
                          minHeight: 38,
                          backgroundColor: "#fff",
                          cursor: "pointer",
                          marginBottom: 0,
                        }}
                        onClick={() => {
                          const el = dateFromInputRef.current;
                          if (el?.showPicker) el.showPicker();
                          else el?.click?.();
                        }}
                      >
                        <i
                          className="fas fa-calendar-alt"
                          style={{ color: "#6c757d" }}
                        />
                        <span style={{ color: "#212529", flex: 1 }}>
                          {formatDateForFilterDisplay(dateFilterFrom) ||
                            "Seleccionar"}
                        </span>
                      </label>
                      <input
                        id="date-filter-from"
                        ref={dateFromInputRef}
                        type="date"
                        value={dateFilterFrom}
                        onChange={(e) => {
                          setDateFilterFrom(e.target.value);
                          setConversationsPage(1);
                        }}
                        style={{
                          position: "absolute",
                          width: 0,
                          height: 0,
                          opacity: 0,
                          overflow: "hidden",
                          clip: "rect(0,0,0,0)",
                        }}
                        aria-label="Fecha desde"
                      />
                    </Col>
                    <Col xs={12} md="auto" className="mt-2 mt-md-0">
                      <Label className="me-2 mb-0" style={{ color: "#212529" }}>
                        Hasta
                      </Label>
                      <label
                        htmlFor="date-filter-to"
                        className="d-flex align-items-center gap-2 border rounded px-2 py-1"
                        style={{
                          minWidth: 140,
                          minHeight: 38,
                          backgroundColor: "#fff",
                          cursor: "pointer",
                          marginBottom: 0,
                        }}
                        onClick={() => {
                          const el = dateToInputRef.current;
                          if (el?.showPicker) el.showPicker();
                          else el?.click?.();
                        }}
                      >
                        <i
                          className="fas fa-calendar-alt"
                          style={{ color: "#6c757d" }}
                        />
                        <span style={{ color: "#212529", flex: 1 }}>
                          {formatDateForFilterDisplay(dateFilterTo) ||
                            "Seleccionar"}
                        </span>
                      </label>
                      <input
                        id="date-filter-to"
                        ref={dateToInputRef}
                        type="date"
                        value={dateFilterTo}
                        onChange={(e) => {
                          setDateFilterTo(e.target.value);
                          setConversationsPage(1);
                        }}
                        style={{
                          position: "absolute",
                          width: 0,
                          height: 0,
                          opacity: 0,
                          overflow: "hidden",
                          clip: "rect(0,0,0,0)",
                        }}
                        aria-label="Fecha hasta"
                      />
                    </Col>
                    <Col xs={12} md="auto" className="mt-2 mt-md-0">
                      <Button
                        color="secondary"
                        size="sm"
                        outline
                        onClick={() => {
                          setDateFilterFrom("");
                          setDateFilterTo("");
                          setTagFilter("");
                          setTagFilterInput("");
                          setPhoneFilter("");
                          setPhoneFilterInput("");
                          setConversationsPage(1);
                        }}
                        disabled={
                          !dateFilterFrom &&
                          !dateFilterTo &&
                          !tagFilterInput &&
                          !phoneFilterInput
                        }
                      >
                        Limpiar filtros
                      </Button>
                    </Col>
                    <Col xs={12} md="auto" className="mt-2 mt-md-0">
                      <Label className="me-2 mb-0" style={{ color: "#212529" }}>
                        Teléfono
                      </Label>
                      <Input
                        type="text"
                        value={phoneFilterInput}
                        onChange={(e) => setPhoneFilterInput(e.target.value)}
                        placeholder="ej. 5215512345678"
                        style={{ maxWidth: 160 }}
                      />
                    </Col>
                    <Col xs={12} md="auto" className="mt-2 mt-md-0">
                      <Label className="me-2 mb-0" style={{ color: "#212529" }}>
                        Etiqueta
                      </Label>
                      <Input
                        type="text"
                        value={tagFilterInput}
                        onChange={(e) => {
                          setTagFilterInput(e.target.value);
                        }}
                        placeholder="ej. Escalada"
                        style={{ maxWidth: 160 }}
                      />
                    </Col>
                    <Col xs={12} md="auto" className="mt-2 mt-md-0">
                      <Label className="me-2 mb-0" style={{ color: "#212529" }}>
                        Registros por página
                      </Label>
                      <Input
                        type="number"
                        min={CONVERSATIONS_PAGE_SIZE_MIN}
                        max={CONVERSATIONS_PAGE_SIZE_MAX}
                        value={conversationsPageSize}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (!Number.isNaN(val)) setConversationsPageSize(val);
                        }}
                        style={{ width: 72 }}
                      />
                    </Col>
                    <Col className="mt-2 mt-md-0">
                      <small className="text-muted">
                        {conversationsTotal > 0
                          ? `${(conversationsPage - 1) * pageSize + 1}–${Math.min(conversationsPage * pageSize, conversationsTotal)} de ${conversationsTotal} conversaciones`
                          : "0 conversaciones"}
                        {dateFilterFrom || dateFilterTo
                          ? " (filtro por fecha de creación)"
                          : ""}
                      </small>
                    </Col>
                    <Col
                      xs={12}
                      md="auto"
                      className="mt-2 mt-md-0 ms-md-auto d-flex flex-wrap gap-2 justify-content-end"
                    >
                      {mantisBusinessId === "66bbb0a2556f62f4d3b49f69" && (
                        <Button
                          color="primary"
                          tag="a"
                          href="https://timetracker.mantisai.info/cliente/92f30368-ac62-40fc-8fe4-62a293843ea1"
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Abrir Time Tracker en nueva pestaña"
                        >
                          <i className="fas fa-clock me-1" />
                          Tiempo Desarrollo
                        </Button>
                      )}
                      <Button
                        color="info"
                        onClick={() => setShowConfigModal(true)}
                        disabled={
                          filteredConversations.length === 0 || isAnalyzing
                        }
                        title="Configurar y analizar conversaciones con IA (usa los registros mostrados)"
                      >
                        <i className="fas fa-brain me-1" />
                        Analizar con IA
                      </Button>
                      <Button
                        color="success"
                        onClick={exportConversationsToExcel}
                        disabled={
                          filteredConversations.length === 0 || exportLoading
                        }
                        title={
                          filteredConversations.length === 0
                            ? "No hay conversaciones para exportar"
                            : "Exportar conversaciones a Excel (registros mostrados)"
                        }
                      >
                        {exportLoading ? (
                          <>
                            <Spinner size="sm" className="me-1" />
                            Exportando...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-file-excel me-1" />
                            Exportar Excel
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>

                  {(dateFilterFrom || dateFilterTo) &&
                    conversationsPerDayChartData.length === 0 &&
                    conversationsTotal === 0 &&
                    !loading && (
                      <p className="text-muted small mb-3">
                        No hay conversaciones en el rango seleccionado. La
                        gráfica aparecerá cuando haya datos.
                      </p>
                    )}
                  {!(dateFilterFrom || dateFilterTo) &&
                    conversationsPerDayChartData.length === 0 && (
                      <p className="text-muted small mb-3">
                        Selecciona un rango de fechas (Desde / Hasta) para ver
                        la tendencia de conversaciones y sesiones creadas por
                        día.
                      </p>
                    )}
                  {conversationsPerDayChartData.length > 0 && (
                    <Card className="mb-4">
                      <CardBody>
                        <h6 className="mb-3" style={{ color: "#212529" }}>
                          Tendencia por día (conversaciones y sesiones)
                          {(dateFilterFrom || dateFilterTo) && (
                            <small className="text-muted fw-normal ms-2">
                              (en el rango de fechas seleccionado)
                            </small>
                          )}
                        </h6>
                        <ResponsiveContainer width="100%" height={280}>
                          <LineChart
                            data={conversationsPerDayChartData}
                            margin={{ top: 5, right: 16, left: 0, bottom: 5 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#e9ecef"
                            />
                            <XAxis
                              dataKey="dateLabel"
                              tick={{ fontSize: 12 }}
                              tickMargin={8}
                            />
                            <YAxis
                              allowDecimals={false}
                              tick={{ fontSize: 12 }}
                              tickMargin={8}
                            />
                            <Tooltip
                              labelFormatter={(_, payload) => {
                                const dateStr = payload?.[0]?.payload?.date;
                                if (!dateStr) return "";
                                const d = new Date(dateStr + "T00:00:00.000Z");
                                return d.toLocaleDateString("es-ES", {
                                  timeZone: "UTC",
                                  weekday: "short",
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                });
                              }}
                              formatter={(value, name) => [value, name]}
                              contentStyle={{ borderRadius: 8 }}
                            />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="count"
                              stroke="#0d6efd"
                              strokeWidth={2}
                              dot={{ fill: "#0d6efd", r: 4 }}
                              activeDot={{ r: 6 }}
                              name="Conversaciones"
                            />
                            {tagFilter && (
                              <Line
                                type="monotone"
                                dataKey="tagCount"
                                stroke="#fd7e14"
                                strokeWidth={2}
                                dot={{ fill: "#fd7e14", r: 4 }}
                                activeDot={{ r: 6 }}
                                name={`Conversaciones (${tagFilter})`}
                              />
                            )}
                            <Line
                              type="monotone"
                              dataKey="sessionsCount"
                              stroke="#198754"
                              strokeWidth={2}
                              dot={{ fill: "#198754", r: 4 }}
                              activeDot={{ r: 6 }}
                              name="Mensajes enviados"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardBody>
                    </Card>
                  )}

                  <div className="table-responsive">
                    <Table size="sm" hover>
                      <thead>
                        <tr>
                          <th>Fecha creación</th>
                          <th>Estado</th>
                          <th>Canal</th>
                          <th>Contacto</th>
                          <th>Etiquetas</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredConversations.length === 0 ? (
                          <tr>
                            <td
                              colSpan={6}
                              className="text-center text-muted py-4"
                            >
                              {dateFilterFrom || dateFilterTo
                                ? "No hay conversaciones en el rango de fechas seleccionado"
                                : "No hay conversaciones"}
                            </td>
                          </tr>
                        ) : (
                          filteredConversations.map((conv) => (
                            <tr key={conv._id}>
                              <td>{formatDate(conv.dateCreated)}</td>
                              <td>
                                <Badge color="info">{conv.status || "—"}</Badge>
                              </td>
                              <td>
                                {conv.channels?.length
                                  ? conv.channels
                                      .map((ch) => ch.type || ch)
                                      .join(", ")
                                  : "—"}
                              </td>
                              <td>
                                {conv.contact?.phoneNumber ||
                                  conv.contact?.name ||
                                  conv.contact?.email ||
                                  conv.contactPhone ||
                                  conv.contactName ||
                                  conv.contactEmail ||
                                  "—"}
                              </td>
                              <td>
                                {conv.tags?.length > 0 ? (
                                  <span className="d-flex flex-wrap">
                                    {conv.tags.map((tag) => (
                                      <Badge
                                        key={tag}
                                        color="secondary"
                                        className="fw-normal me-1 mb-1"
                                      >
                                        {tag}
                                      </Badge>
                                    ))}
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => openConversationDetail(conv)}
                                >
                                  Ver
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {conversationsTotal > pageSize && (
                    <Row className="mt-3 align-items-center">
                      <Col>
                        <small className="text-muted">
                          Página {conversationsPage} de{" "}
                          {Math.ceil(conversationsTotal / pageSize) || 1}
                        </small>
                      </Col>
                      <Col xs="auto">
                        <Button
                          color="secondary"
                          size="sm"
                          outline
                          disabled={conversationsPage <= 1 || loading}
                          onClick={() =>
                            setConversationsPage((p) => Math.max(1, p - 1))
                          }
                        >
                          Anterior
                        </Button>
                        <Button
                          color="secondary"
                          size="sm"
                          outline
                          className="ms-2"
                          disabled={
                            loading ||
                            conversationsPage >=
                              Math.ceil(conversationsTotal / pageSize)
                          }
                          onClick={() => setConversationsPage((p) => p + 1)}
                        >
                          Siguiente
                        </Button>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </TabPane>
      </TabContent>

      {/* Bulk Template (Envío masivo) Modal */}
      <Modal
        isOpen={bulkTemplateModal}
        toggle={closeBulkTemplateModal}
        size="lg"
        backdrop="static"
      >
        <ModalHeader toggle={closeBulkTemplateModal}>
          <i className="fas fa-paper-plane me-2" />
          Envío Masivo de Templates
        </ModalHeader>
        <ModalBody>
          {bulkTemplateLoading && (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted">
                  Procesando lote {bulkTemplateProgress.currentBatch} de{" "}
                  {bulkTemplateProgress.totalBatches}
                </span>
                <span className="text-muted">
                  {bulkTemplateProgress.currentSent +
                    bulkTemplateProgress.currentFailed}{" "}
                  / {bulkTemplateProgress.totalRecipients} destinatarios
                </span>
              </div>
              <Progress
                value={
                  bulkTemplateProgress.totalBatches > 0
                    ? Math.min(
                        (bulkTemplateProgress.currentBatch /
                          bulkTemplateProgress.totalBatches) *
                          100,
                        100,
                      )
                    : 0
                }
                color="primary"
                className="mb-2 bulk-template-progress"
                style={{ height: "24px" }}
                animated
                striped
              >
                {bulkTemplateProgress.totalBatches > 0
                  ? Math.round(
                      Math.min(
                        (bulkTemplateProgress.currentBatch /
                          bulkTemplateProgress.totalBatches) *
                          100,
                        100,
                      ),
                    )
                  : 0}
                %
              </Progress>
              <div className="d-flex justify-content-between small text-muted">
                <span>
                  <i className="fas fa-check-circle text-success me-1" />
                  Exitosos: {bulkTemplateProgress.currentSent}
                </span>
                <span>
                  <i className="fas fa-times-circle text-danger me-1" />
                  Fallidos: {bulkTemplateProgress.currentFailed}
                </span>
              </div>
            </div>
          )}

          {bulkTemplateError && (
            <Alert color="danger" className="mb-3">
              {bulkTemplateError}
            </Alert>
          )}

          {bulkTemplateSuccess && (
            <Alert color="success" className="mb-3">
              {bulkTemplateSuccess}
              {bulkTemplateResults && (
                <div className="mt-2">
                  <Button
                    color="link"
                    size="sm"
                    className="p-0 text-decoration-none"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowDetailedResults(!showDetailedResults);
                    }}
                    type="button"
                  >
                    {showDetailedResults ? (
                      <>
                        <i className="fas fa-chevron-up me-1" />
                        Ocultar detalles
                      </>
                    ) : (
                      <>
                        <i className="fas fa-chevron-down me-1" />
                        Ver detalles
                      </>
                    )}
                  </Button>
                </div>
              )}
            </Alert>
          )}

          {bulkTemplateResults && showDetailedResults && (
            <div className="mb-3" onClick={(e) => e.stopPropagation()}>
              {bulkTemplateResults.successfulPhoneNumbers?.length > 0 && (
                <div className="mb-3">
                  <h6 className="text-success">
                    <i className="fas fa-check-circle me-2" />
                    Exitosos (
                    {bulkTemplateResults.successfulPhoneNumbers.length}):
                  </h6>
                  <div
                    className="border rounded p-2 bg-light"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    <ul
                      className="mb-0"
                      style={{ listStyle: "none", paddingLeft: 0 }}
                    >
                      {bulkTemplateResults.successfulPhoneNumbers.map(
                        (item, index) => (
                          <li key={index} className="mb-1">
                            <span className="badge bg-success me-2">
                              <i className="fas fa-check" />
                            </span>
                            {item.phoneNumber}
                            {item.timestamp && (
                              <small className="text-muted ms-2">
                                ({new Date(item.timestamp).toLocaleString()})
                              </small>
                            )}
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                </div>
              )}
              {bulkTemplateResults.failedPhoneNumbers?.length > 0 && (
                <div className="mb-3">
                  <h6 className="text-danger">
                    <i className="fas fa-times-circle me-2" />
                    Fallidos ({bulkTemplateResults.failedPhoneNumbers.length}):
                  </h6>
                  <div
                    className="border rounded p-2 bg-light"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    <ul
                      className="mb-0"
                      style={{ listStyle: "none", paddingLeft: 0 }}
                    >
                      {bulkTemplateResults.failedPhoneNumbers.map(
                        (item, index) => (
                          <li key={index} className="mb-2">
                            <div>
                              <span className="badge bg-danger me-2">
                                <i className="fas fa-times" />
                              </span>
                              <strong>{item.phoneNumber}</strong>
                              {item.timestamp && (
                                <small className="text-muted ms-2">
                                  ({new Date(item.timestamp).toLocaleString()})
                                </small>
                              )}
                            </div>
                            {item.error && (
                              <div className="ms-4 text-danger small">
                                <small>{item.error}</small>
                              </div>
                            )}
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                </div>
              )}
              {bulkTemplateResults.errors?.length > 0 && (
                <div className="mb-3">
                  <h6 className="text-warning">
                    <i className="fas fa-exclamation-triangle me-2" />
                    Errores adicionales:
                  </h6>
                  <div
                    className="border rounded p-2 bg-light"
                    style={{ maxHeight: "150px", overflowY: "auto" }}
                  >
                    <ul className="mb-0 small">
                      {bulkTemplateResults.errors.map((error, index) => (
                        <li key={index} className="text-warning">
                          {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          <Form>
            {bulkTemplateAgent?.whatsappAccounts?.length > 1 &&
              !selectedWhatsAppAccount && (
                <FormGroup className="mb-3">
                  <Label>Cuenta de WhatsApp *</Label>
                  <Input
                    type="select"
                    value={
                      selectedWhatsAppAccount
                        ? bulkTemplateAgent.whatsappAccounts.indexOf(
                            selectedWhatsAppAccount,
                          )
                        : ""
                    }
                    onChange={(e) => {
                      const idx = e.target.value;
                      if (idx !== "")
                        setSelectedWhatsAppAccount(
                          bulkTemplateAgent.whatsappAccounts[parseInt(idx, 10)],
                        );
                    }}
                  >
                    <option value="">Selecciona una cuenta</option>
                    {bulkTemplateAgent.whatsappAccounts.map((acc, idx) => (
                      <option key={idx} value={idx}>
                        {acc.accountName || acc.phoneId || `Cuenta ${idx + 1}`}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              )}
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="templateName">Nombre del Template *</Label>
                  <Input
                    id="templateName"
                    type="text"
                    value={bulkTemplateForm.templateName}
                    onChange={(e) =>
                      handleBulkTemplateFormChange(
                        "templateName",
                        e.target.value,
                      )
                    }
                    placeholder="Nombre del template de WhatsApp"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="templateLanguage">Idioma</Label>
                  <Input
                    id="templateLanguage"
                    type="select"
                    value={bulkTemplateForm.language}
                    onChange={(e) =>
                      handleBulkTemplateFormChange("language", e.target.value)
                    }
                  >
                    <option value="es_MX">Español (México)</option>
                    <option value="es_ES">Español (España)</option>
                    <option value="es_CO">Español (Colombia)</option>
                    <option value="en_US">English (US)</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label for="excelFile">Archivo Excel *</Label>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Input
                  id="excelFile"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) =>
                    handleBulkTemplateFormChange("excelFile", e.target.files[0])
                  }
                  required
                />
                <Button
                  color="info"
                  size="sm"
                  onClick={downloadTemplate}
                  title="Descargar plantilla de ejemplo"
                >
                  <i className="fas fa-download me-1" />
                  Descargar Plantilla
                </Button>
              </div>
              <small className="text-muted">
                <strong>Formato requerido:</strong>
                <br />
                • Fila 1: Nombres de columnas (ej: Telefono, Nombre, Email,
                etc.)
                <br />
                • Fila 2: Tipo de dato (PARAMETER para parámetros del template,
                CONTEXT para información del agente)
                <br />
                • Fila 3+: Datos de los destinatarios
                <br />
                <strong>Nota:</strong> La columna de teléfono debe estar marcada
                como PARAMETER
              </small>
            </FormGroup>
            {selectedWhatsAppAccount && (
              <Alert color="info" className="mt-3">
                <strong>Cuenta WhatsApp seleccionada:</strong>{" "}
                {selectedWhatsAppAccount.accountName}
                <br />
                <strong>Phone ID:</strong> {selectedWhatsAppAccount.phoneId}
              </Alert>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeBulkTemplateModal}>
            {bulkTemplateLoading ? "Ocultar (el envío continúa)" : "Cancelar"}
          </Button>
          <Button
            color="primary"
            onClick={handleBulkTemplateSubmit}
            disabled={
              bulkTemplateLoading ||
              !bulkTemplateForm.templateName.trim() ||
              !bulkTemplateForm.excelFile ||
              !selectedWhatsAppAccount
            }
          >
            {bulkTemplateLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Enviando...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane me-2" />
                Enviar Templates
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Configurar Análisis con IA Modal */}
      <Modal
        isOpen={showConfigModal}
        toggle={() => setShowConfigModal(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setShowConfigModal(false)}>
          <i className="fas fa-cog me-2" />
          Configurar Análisis con IA
        </ModalHeader>
        <ModalBody>
          <Form>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="analysisModel">
                    <strong>Modelo de IA</strong>
                  </Label>
                  <Input
                    id="analysisModel"
                    type="select"
                    value={analysisConfig.model}
                    onChange={(e) =>
                      setAnalysisConfig({
                        ...analysisConfig,
                        model: e.target.value,
                      })
                    }
                  >
                    <option value="us.anthropic.claude-haiku-4-5-20251001-v1:0">Claude Haiku 4.5</option>
                    {/* <option value="gemini-2.5-pro">Gemini 2.5 Pro</option>
                    <option value="gemini-2.0-flash">Gemini 2.0 Flash</option> */}
                  </Input>
                  <small className="text-muted">
                    Selecciona el modelo de IA para el análisis
                  </small>
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label for="analysisPrompt">
                <strong>Prompt para el Análisis</strong>
              </Label>
              <Input
                id="analysisPrompt"
                type="textarea"
                rows={15}
                value={analysisConfig.prompt}
                onChange={(e) =>
                  setAnalysisConfig({
                    ...analysisConfig,
                    prompt: e.target.value,
                  })
                }
                placeholder="Ingresa el prompt que se usará para analizar cada conversación..."
              />
              <small className="text-muted">
                El prompt debe incluir instrucciones sobre el formato de
                respuesta esperado (preferiblemente JSON).
              </small>
            </FormGroup>
            <hr />
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Label>
                  <strong>Columnas de Salida en Excel</strong>
                </Label>
                <Button
                  color="success"
                  size="sm"
                  onClick={handleAddCustomColumn}
                >
                  <i className="fas fa-plus me-1" />
                  Agregar Columna Personalizada
                </Button>
              </div>
              <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                {analysisConfig.outputColumns.map((column, index) => (
                  <div
                    key={index}
                    className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded"
                  >
                    <div className="form-check">
                      <Input
                        type="checkbox"
                        id={`column-${index}`}
                        checked={column.enabled}
                        onChange={() => handleColumnToggle(index)}
                        className="form-check-input"
                      />
                      <Label
                        for={`column-${index}`}
                        className="form-check-label ms-2"
                        style={{ color: "#212529" }}
                      >
                        {column.label}{" "}
                        <small style={{ color: "#495057" }}>
                          ({column.key})
                        </small>
                      </Label>
                    </div>
                    {index >= 12 && (
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleRemoveCustomColumn(index)}
                      >
                        <i className="fas fa-trash" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowConfigModal(false)}>
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={startAnalysis}
            disabled={!analysisConfig.prompt.trim()}
          >
            <i className="fas fa-play me-1" />
            Iniciar Análisis
          </Button>
        </ModalFooter>
      </Modal>

      {/* Análisis en progreso / resultados Modal */}
      <Modal
        isOpen={showAnalysisModal}
        toggle={() => {
          if (!isAnalyzing) setShowAnalysisModal(false);
        }}
        size="lg"
      >
        <ModalHeader toggle={() => !isAnalyzing && setShowAnalysisModal(false)}>
          <i className="fas fa-brain me-2" />
          Análisis de Conversaciones con IA
        </ModalHeader>
        <ModalBody>
          {isAnalyzing ? (
            <div>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <span>
                    Progreso: {analysisProgress.current} /{" "}
                    {analysisProgress.total}
                  </span>
                  <span>{analysisProgress.percentage || 0}%</span>
                </div>
                <div
                  className="progress analysis-progress mb-2"
                  style={{ height: "25px" }}
                >
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: `${analysisProgress.percentage || 0}%` }}
                    aria-valuenow={analysisProgress.percentage || 0}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    {analysisProgress.percentage || 0}%
                  </div>
                </div>
              </div>
              {batchErrors.length > 0 && (
                <Alert color="warning">
                  <strong>Errores:</strong> {batchErrors.length} conversaciones
                  tuvieron errores
                </Alert>
              )}
              <div className="mt-3">
                <small className="text-muted">
                  Analizando conversaciones... Esto puede tomar varios minutos
                  dependiendo de la cantidad.
                </small>
              </div>
            </div>
          ) : (
            <div>
              <Alert color="success">
                <strong>Análisis completado!</strong>
                <br />
                {batchResults.length} conversaciones analizadas exitosamente
                {batchErrors.length > 0 && (
                  <>
                    <br />
                    {batchErrors.length} conversaciones con errores
                  </>
                )}
              </Alert>
              {batchResults.length > 0 && (
                <Button
                  color="success"
                  onClick={() => exportAnalysisToExcel()}
                  className="mt-3"
                  block
                >
                  <i className="fas fa-file-excel me-1" />
                  Exportar Resultados a Excel
                </Button>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {isAnalyzing ? (
            <>
              {isAnalysisPaused ? (
                <Button color="success" onClick={resumeAnalysis}>
                  <i className="fas fa-play me-1" />
                  Reanudar
                </Button>
              ) : (
                <Button color="warning" onClick={pauseAnalysis}>
                  <i className="fas fa-pause me-1" />
                  Pausar
                </Button>
              )}
              <Button color="danger" onClick={cancelAnalysis}>
                <i className="fas fa-stop me-1" />
                Cancelar
              </Button>
            </>
          ) : (
            <Button
              color="secondary"
              onClick={() => setShowAnalysisModal(false)}
            >
              Cerrar
            </Button>
          )}
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={conversationModal}
        toggle={closeConversationDetail}
        size="lg"
      >
        <ModalHeader toggle={closeConversationDetail}>
          Detalle de conversación
        </ModalHeader>
        <ModalBody>
          {detailsLoading ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner />
            </div>
          ) : selectedConversation ? (
            <>
              <div className="mb-3 pb-2 border-bottom small">
                {selectedConversation.tags?.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    {selectedConversation.tags.map((tag) => (
                      <Badge key={tag} color="secondary" className="fw-normal">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                  <strong className="text-muted" style={{ minWidth: 72 }}>
                    ID:
                  </strong>
                  <span
                    className="text-dark"
                    style={{ wordBreak: "break-all", minWidth: 0 }}
                  >
                    {selectedConversation._id}
                  </span>
                  <Badge color="info">{selectedConversation.status}</Badge>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <strong className="text-muted" style={{ minWidth: 72 }}>
                    Contacto:
                  </strong>
                  <span className="min-w-0">
                    {[
                      selectedConversation.contact?.name ??
                        selectedConversation.contactName,
                      selectedConversation.contact?.email ??
                        selectedConversation.contactEmail,
                      selectedConversation.contact?.phoneNumber ??
                        selectedConversation.contactPhone,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </span>
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                  Mensajes ({conversationMessages.length})
                </h6>
                {conversationMessages.length > 0 && !isScrolledToBottom && (
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={scrollToLastMessage}
                    title="Ir al último mensaje"
                  >
                    <i className="fas fa-chevron-down me-1" />
                    Último mensaje
                  </Button>
                )}
              </div>
              <div
                ref={messagesScrollRef}
                onScroll={checkScrollPosition}
                className="rounded p-3 mb-3"
                style={{
                  maxHeight: 380,
                  overflowY: "auto",
                  backgroundColor: "#f0f2f5",
                  minHeight: 120,
                }}
              >
                {conversationMessages.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    Sin mensajes
                  </div>
                ) : (
                  conversationMessages.map((msg, i) => {
                    const isUser = msg.sender === "USER";
                    const senderLabel =
                      msg.sender === "AGENT"
                        ? "Agente"
                        : msg.sender === "MANTIS"
                          ? "Mantis"
                          : "Usuario";
                    const content =
                      msg.content?.text ||
                      msg.content?.html ||
                      (typeof msg.content === "string" ? msg.content : null) ||
                      (msg.content ? JSON.stringify(msg.content) : "");
                    return (
                      <div
                        key={i}
                        className={`d-flex mb-3 ${isUser ? "justify-content-end" : "justify-content-start"}`}
                      >
                        <div
                          className="rounded-3 px-3 py-1 shadow-sm text-start"
                          style={{
                            maxWidth: "95%",
                            minWidth: "60%",
                            backgroundColor: isUser ? "#0d6efd" : "#fff",
                            color: isUser ? "#fff" : "#1f2937",
                            border: isUser ? "none" : "1px solid #e5e7eb",
                            textAlign: "left",
                          }}
                        >
                          <div
                            className="small fw-semibold mb-0"
                            style={{ opacity: isUser ? 0.9 : 1 }}
                          >
                            {senderLabel}
                          </div>
                          <div
                            className="small"
                            style={{
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              textAlign: "left",
                            }}
                          >
                            {content}
                          </div>
                          <div
                            className="small mt-0"
                            style={{
                              opacity: 0.75,
                              fontSize: "0.7rem",
                            }}
                          >
                            {formatDate(msg.dateCreated)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <h6 className="mb-2">Sesiones ({conversationSessions.length})</h6>
              <div
                className="border rounded p-2"
                style={{
                  maxHeight: 140,
                  overflowY: "auto",
                  backgroundColor: "#fafafa",
                }}
              >
                {conversationSessions.length === 0 ? (
                  <span className="text-muted small">Sin sesiones</span>
                ) : (
                  <ul className="mb-0 small ps-3">
                    {conversationSessions.map((s, i) => (
                      <li key={i}>
                        <span className="text-muted">
                          {s.sessionId || s._id}
                        </span>
                        {" — "}
                        {formatDate(s.dateCreated)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          ) : null}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default BusinessAgentsSection;
